import React from 'react';

const Resources = ({ data }) => {
    return (
        <section className="py-20 bg-stone-50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-stone-800 mb-6">
                        Resources
                    </h2>
                    <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
                        Helpful tools, information, and support resources to complement your therapeutic journey.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                    {data.map((resource) => (
                        <div
                            key={resource.id}
                            className="bg-white rounded-2xl p-8 shadow-sm border border-stone-100"
                        >
                            <div className="flex items-start space-x-4 mb-6">
                                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-semibold text-stone-800 mb-2">
                                        {resource.title}
                                    </h3>
                                    <p className="text-stone-600 mb-4">
                                        {resource.description}
                                    </p>
                                </div>
                            </div>

                            <ul className="space-y-3">
                                {resource.links.map((link, index) => (
                                    <li key={index} className="flex items-center space-x-3">
                                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                                        <span className="text-stone-700">{link}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Additional Resource Categories */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-stone-100">
                        <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-stone-800 mb-3">Recommended Reading</h3>
                        <p className="text-stone-600 text-sm">Curated books and articles for personal growth and understanding</p>
                    </div>

                    <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-stone-100">
                        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-stone-800 mb-3">Wellness Tools</h3>
                        <p className="text-stone-600 text-sm">Practical exercises and techniques for daily mental wellness</p>
                    </div>

                    <div className="bg-white rounded-2xl p-6 text-center shadow-sm border border-stone-100">
                        <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-stone-800 mb-3">Support Groups</h3>
                        <p className="text-stone-600 text-sm">Community connections and peer support opportunities</p>
                    </div>
                </div>

                {/* Emergency Resources */}
                <div className="mt-16 bg-amber-50 border border-amber-200 rounded-2xl p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-semibold text-amber-800 mb-4">Crisis Support</h3>
                        <p className="text-amber-700 mb-6">If you're experiencing a mental health crisis, please reach out for immediate help.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl p-6 text-center">
                            <h4 className="font-semibold text-stone-800 mb-2">National Suicide Prevention Lifeline</h4>
                            <p className="text-2xl font-bold text-stone-800 mb-2">988</p>
                            <p className="text-stone-600 text-sm">24/7 free and confidential support</p>
                        </div>

                        <div className="bg-white rounded-xl p-6 text-center">
                            <h4 className="font-semibold text-stone-800 mb-2">Crisis Text Line</h4>
                            <p className="text-xl font-bold text-stone-800 mb-2">Text HOME to 741741</p>
                            <p className="text-stone-600 text-sm">24/7 crisis support via text</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Resources;